import {
  parseISO,
  dayOfWeek,
  DAY_NAMES_ID,
  MONTH_NAMES_ID,
  dateSlug,
  addDays,
} from "@/lib/calendar/dates";
import type { Holiday } from "@/data/holidays";

const DAY_NAMES_EN = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];
const MONTH_NAMES_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface LongWeekend {
  /** Range start ISO */
  startISO: string;
  /** Range end ISO */
  endISO: string;
  /** Total consecutive off-days */
  totalDays: number;
  /** Trigger holidays inside the range */
  triggers: Holiday[];
  /** Optional cuti note (cuti bersama / saran tambahan) */
  notes: string[];
  /**
   * "long-weekend" = libur 3+ hari beruntun terbentuk alami.
   * "harpitnas"    = ada 1 hari kerja terjepit di antara dua hari libur,
   *                  yang biasanya di-cuti-kan untuk memperpanjang libur.
   */
  category: "long-weekend" | "harpitnas";
  /** ISO of the single squeezed working day, if category === "harpitnas" */
  harpitISO?: string;
}

/**
 * Detect long weekends from holiday + Sunday + cuti-bersama clusters.
 * Naive grouping: any continuous run of (Sunday | libur | cuti-bersama)
 * with totalDays >= 3 counts as a long weekend.
 */
export function detectLongWeekends(year: number, holidays: Holiday[]): LongWeekend[] {
  const isOff = new Map<string, Holiday[]>();
  for (const h of holidays) {
    const arr = isOff.get(h.date) ?? [];
    arr.push(h);
    isOff.set(h.date, arr);
  }
  const results: LongWeekend[] = [];
  const isOffDay = (iso: string) => {
    const p = parseISO(iso)!;
    const dow = dayOfWeek(p.y, p.m, p.d);
    if (dow === 0 || dow === 6) return true;
    const list = isOff.get(iso);
    return !!list && list.some((h) => h.type === "libur" || h.type === "cuti-bersama");
  };
  const collectTriggers = (a: string, b: string): Holiday[] => {
    const triggers: Holiday[] = [];
    let scan = a;
    while (scan <= b) {
      const list = isOff.get(scan);
      if (list) triggers.push(...list);
      scan = addDays(scan, 1);
    }
    return dedupeByName(triggers);
  };

  // Pass 1: natural long weekends (3+ continuous off days).
  let cur = `${year}-01-01`;
  const end = `${year}-12-31`;
  while (cur <= end) {
    if (isOffDay(cur)) {
      let runEnd = cur;
      while (true) {
        const next = addDays(runEnd, 1);
        if (next > end) break;
        if (!isOffDay(next)) break;
        runEnd = next;
      }
      const totalDays = countDaysInclusive(cur, runEnd);
      if (totalDays >= 3) {
        results.push({
          startISO: cur,
          endISO: runEnd,
          totalDays,
          triggers: collectTriggers(cur, runEnd),
          notes: [],
          category: "long-weekend",
        });
      }
      cur = addDays(runEnd, 1);
    } else {
      cur = addDays(cur, 1);
    }
  }

  // Pass 2: harpitnas — single working day squeezed between two off days.
  // Skip days already covered by an existing long weekend, and ignore
  // Saturday since recommending cuti on a Saturday makes no sense.
  const inLW = (iso: string) =>
    results.some((lw) => iso >= lw.startISO && iso <= lw.endISO);
  cur = `${year}-01-01`;
  while (cur <= end) {
    if (!inLW(cur) && !isOffDay(cur)) {
      const p = parseISO(cur)!;
      const dow = dayOfWeek(p.y, p.m, p.d);
      if (dow !== 6) {
        const prev = addDays(cur, -1);
        const next = addDays(cur, 1);
        if (prev >= `${year}-01-01` && next <= end && isOffDay(prev) && isOffDay(next)) {
          // expand the surrounding off-runs to compute total span if cuti is taken
          let a = prev;
          while (true) {
            const n = addDays(a, -1);
            if (n < `${year}-01-01` || !isOffDay(n)) break;
            a = n;
          }
          let b = next;
          while (true) {
            const n = addDays(b, 1);
            if (n > end || !isOffDay(n)) break;
            b = n;
          }
          results.push({
            startISO: a,
            endISO: b,
            totalDays: countDaysInclusive(a, b),
            triggers: collectTriggers(a, b),
            notes: [`Ambil cuti tanggal ${cur.slice(8, 10)} untuk menyambung libur.`],
            category: "harpitnas",
            harpitISO: cur,
          });
        }
      }
    }
    cur = addDays(cur, 1);
  }

  // Sort chronologically.
  results.sort((a, b) => a.startISO.localeCompare(b.startISO));
  return results;
}

function countDaysInclusive(aISO: string, bISO: string): number {
  let n = 0;
  let cur = aISO;
  while (cur <= bISO) {
    n++;
    cur = addDays(cur, 1);
  }
  return n;
}

function dedupeByName(items: Holiday[]): Holiday[] {
  const seen = new Set<string>();
  const out: Holiday[] = [];
  for (const h of items) {
    const k = `${h.date}-${h.name}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(h);
  }
  return out;
}

export function formatLongWeekendRange(lw: LongWeekend, locale: "id" | "en" = "id"): string {
  const a = parseISO(lw.startISO)!;
  const b = parseISO(lw.endISO)!;
  const days = locale === "en" ? DAY_NAMES_EN : DAY_NAMES_ID;
  const months = locale === "en" ? MONTH_NAMES_EN : MONTH_NAMES_ID;
  const dayA = `${days[dayOfWeek(a.y, a.m, a.d)]} ${a.d} ${months[a.m - 1]}`;
  const dayB = `${days[dayOfWeek(b.y, b.m, b.d)]} ${b.d} ${months[b.m - 1]}`;
  return `${dayA} - ${dayB}`;
}

export function longWeekendStartSlug(lw: LongWeekend): string {
  const a = parseISO(lw.startISO)!;
  return dateSlug(a.y, a.m, a.d);
}

/** Build FAQ JSON-LD from a list of {q,a} */
export function buildFaqLd(items: { q: string; a: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}